// Cliente de impresión térmica vía QZ Tray (agente local que corre en la PC del local).
// El navegador le habla por WebSocket a localhost (nada de webhooks ni puertos públicos)
// y le manda ESC/POS crudo. Ver docs/spec-cafeteria.md (impresión de comandas).
import qz from 'qz-tray';

const config = process.env;
let connecting = null;
let securityPromise = null;

// Nombre de la impresora en el SO. Config por estación en localStorage ('qzPrinter'),
// con fallback a variable de entorno. Si no hay ninguno, el POS usa el camino HTML.
export function getPrinterName() {
    return (localStorage.getItem('qzPrinter') || config.REACT_APP_QZ_PRINTER || '').trim();
}

export function setPrinterName(name) {
    if (name) {
        localStorage.setItem('qzPrinter', name);
    } else {
        localStorage.removeItem('qzPrinter');
    }
}

// Firma de peticiones. El BACKEND guarda la clave privada (nunca toca el browser) y
// sirve el certificado público por /print/cert; con cert => modo FIRMADO. Sin cert
// => modo sin firmar. Fallback a REACT_APP_QZ_CERT por compatibilidad.
//
// ⚠️ FIRMADO ≠ SILENCIOSO. Nuestro certificado es AUTOFIRMADO: QZ lo acepta para
// verificar la firma, pero lo considera *untrusted* y **no deja recordar el allow**
// (al tildar "Remember this decision" deshabilita el botón Allow). Para que no
// aparezca la ventanita hay que instalar el cert como override de confianza EN CADA
// PC — `C:\Program Files\QZ Tray\override.crt` — ver el Paso 4 de
// backend/docs/runbook-implantacion-impresora.md. Firmar igual es necesario: sin
// firma QZ pregunta siempre y el override no alcanza.
//
// ⚠️ El backend responde errores como HTTP 200 con JSON ({error:1002,...}): r.ok no
// alcanza. Solo se acepta como certificado un body que parezca PEM; un JSON de error
// o un fallo de red se tratan como FALLO TRANSITORIO (rechaza) y NO se cachean —
// sin esto, un blip en el primer print dejaba la estación en modo sin-firmar (o con
// un "cert" basura) hasta recargar la página.
function fetchCert() {
    const envCert = (config.REACT_APP_QZ_CERT || '').replace(/\\n/g, '\n').trim();
    if (envCert) {
        return Promise.resolve(envCert);
    }
    return fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'print/cert', {
        headers: { 'access-token': localStorage.getItem('token') },
    })
        .then((r) => {
            if (!r.ok) {
                throw new Error('cert http ' + r.status);
            }
            return r.text();
        })
        .then((text) => {
            const cert = (text || '').trim();
            if (cert === '') {
                return ''; // backend sin QZ_CERT configurado: modo sin firmar legítimo
            }
            if (!cert.startsWith('-----BEGIN')) {
                throw new Error('cert inválido (¿JSON de error del backend?)');
            }
            return cert;
        });
}

function installSecurity(cert) {
    qz.security.setSignatureAlgorithm('SHA512');
    if (cert) {
        qz.security.setCertificatePromise((resolve) => resolve(cert));
        qz.security.setSignaturePromise((dataToSign) => (resolve, reject) => {
            fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'print/sign', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
                body: JSON.stringify({ data: dataToSign }),
            })
                .then((r) => (r.ok ? r.text() : Promise.reject(new Error('sign http ' + r.status))))
                .then((sig) => {
                    // Un JSON de error del backend (200 + {error:...}) NO es una firma.
                    if ((sig || '').trim().startsWith('{')) {
                        return Promise.reject(new Error('sign devolvió error de API'));
                    }
                    return resolve(sig);
                })
                .catch(reject);
        });
    } else {
        qz.security.setCertificatePromise((resolve, reject) => reject());
        qz.security.setSignaturePromise(() => (resolve) => resolve());
    }
}

// Memoizada (una sola config por sesión) y REINTENTABLE: si el fetch del cert falla,
// se instala modo sin-firmar SOLO para este intento y el próximo connect() vuelve a
// intentar el modo firmado. Dos llamadas concurrentes comparten la misma promesa
// (sin doble setCertificatePromise pisándose).
function ensureSecurity() {
    if (!securityPromise) {
        securityPromise = fetchCert()
            .then((cert) => {
                installSecurity(cert);
            })
            .catch((e) => {
                // eslint-disable-next-line no-console
                console.warn('[qz] no se pudo obtener el certificado de firma; sigo sin firmar:', e);
                installSecurity('');
                securityPromise = null; // fallo transitorio: reintentar en el próximo connect
            });
    }
    return securityPromise;
}

// Conexión idempotente (con reintentos). QZ Tray tiene que estar corriendo en la PC.
export function connect() {
    return ensureSecurity().then(() => {
        if (qz.websocket.isActive()) {
            return Promise.resolve();
        }
        if (!connecting) {
            connecting = qz.websocket
                .connect({ retries: 2, delay: 1 })
                .finally(() => { connecting = null; });
        }
        return connecting;
    });
}

export function isConnected() {
    return qz.websocket.isActive();
}

export function listPrinters() {
    return connect().then(() => qz.printers.find());
}

// Imprime ESC/POS crudo (hex) en la impresora indicada.
export function printRawHex(printerName, hexData) {
    return connect().then(() => {
        const cfg = qz.configs.create(printerName, { encoding: 'ISO-8859-1' });
        return qz.print(cfg, [{ type: 'raw', format: 'hex', data: hexData }]);
    });
}
